// C:\Projects\texe-web\src\pages\Notifications.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import {
  API_BASE_URL,
  apiGet,
  openRealtimeStream,
  markNotificationRead,
  markAllNotificationsRead,
} from "../lib/api";
import NotificationItem from "../components/NotificationItem";
import type { NotificationItem as NotifBase } from "../lib/api";

// 🔽 İyimser rozet helpers
import {
  optimisticMarkOneRead,
  optimisticMarkAllRead,
} from "../context/NotificationsBadgeStore";

// Bu sayfada NotificationItem bileşeninin avatar önizlemesini desteklemek için
// actor.avatarUrl / actor.avatarUrlSmall alanlarını normalize ediyoruz.
type Notif = NotifBase & {
  actor:
    | (NotifBase["actor"] & {
        avatarUrl?: string | null;
        avatarUrlSmall?: string | null;
      })
    | null;
};

function absUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  return url.startsWith("/") ? `${API_BASE_URL}${url}` : url;
}

export default function NotificationsPage() {
  const [items, setItems] = useState<Notif[]>([]);
  const [nextAfter, setNextAfter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ids = useRef(new Set<string>());
  const sseRef = useRef<ReturnType<typeof openRealtimeStream> | null>(null);
  const [newCount, setNewCount] = useState(0);

  // ⬇️ “yeni geldi” parlama için id’leri kısa süreli tutacağımız set
  const [flashIds, setFlashIds] = useState<Set<string>>(new Set());

  // eşzamanlı iki load çağrısını engellemek için basit guard
  const loadingRef = useRef(false);

  const title = useMemo(
    () => (unreadOnly ? "🔔 Bildirimler (Okunmamışlar)" : "🔔 Bildirimler"),
    [unreadOnly]
  );

  // Bildirimleri eklerken actor avatar URL’lerini normalize et
  function normalizeList(newItems: NotifBase[]): Notif[] {
    return newItems.map((n) => ({
      ...n,
      actor: n.actor
        ? {
            ...n.actor,
            avatarUrl: absUrl((n.actor as any)?.avatarUrl) ?? null,
            avatarUrlSmall:
              absUrl((n.actor as any)?.avatarUrlSmall) ??
              absUrl((n.actor as any)?.avatarUrl) ??
              null,
          }
        : null,
    }));
  }

  /**
   * Listeyi birleştirir.
   * replace=true → baştan yükleme. Bu dalda “flash” için eski id set’ini (prevIds) kullanır.
   */
  function mergeItems(newItems: NotifBase[], replace = false, prevIds?: Set<string>) {
    const normalized = normalizeList(newItems);

    if (replace) {
      // “taze” olanları belirle (önceden listede yoksa)
      const prev = prevIds ?? ids.current;
      const fresh = normalized.filter((n) => !prev.has(n.id)).map((n) => n.id);

      ids.current = new Set(normalized.map((n) => n.id));
      setItems(normalized);

      if (fresh.length) {
        setFlashIds((old) => {
          const next = new Set(old);
          for (const id of fresh) next.add(id);
          return next;
        });
        // 1.5 sn sonra parlama bayraklarını temizle
        setTimeout(() => {
          setFlashIds((old) => {
            const next = new Set(old);
            for (const id of fresh) next.delete(id);
            return next;
          });
        }, 1500);
      }
      return;
    }

    // replace=false → sona ekle (tekrarı engelle)
    setItems((curr) => {
      const merged: Notif[] = [...curr];
      for (const n of normalized) {
        if (!ids.current.has(n.id)) {
          ids.current.add(n.id);
          merged.push(n);
        }
      }
      return merged;
    });
  }

  async function load(initial = false) {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const base = `/api/notifications`;
      const qsParts: string[] = [];
      if (!initial && nextAfter) qsParts.push(`after=${encodeURIComponent(nextAfter)}`);
      if (unreadOnly) qsParts.push(`unread=1`);
      const qs = qsParts.length ? `?${qsParts.join("&")}` : "";

      // not: tip kesinliği için generic’i koruyoruz
      const res = await apiGet<{ ok: true; items: NotifBase[]; nextAfter: string | null }>(
        `${base}${qs}`
      );

      if (initial) {
        // “Yenile” aksiyonunda tazeleri yakalamak için önceki id’lerin snapshot’unu geçir
        const prevSnap = new Set(ids.current);
        mergeItems(res.items, true, prevSnap);
      } else {
        mergeItems(res.items, false);
      }
      setNextAfter(res.nextAfter);
    } finally {
      loadingRef.current = false;
    }
  }

  // İlk yükleme + unreadOnly değişince yeniden yükle
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      ids.current.clear(); // ilk açılışta flash gerekmiyor
      try {
        await load(true);
      } catch (e: any) {
        console.error(e);
        setError("Bildirimler yüklenemedi.");
        setItems([]);
        setNextAfter(null);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadOnly]);

  async function readOne(id: string) {
    // 🔍 önce gerçekten okunmamış mı bak
    const target = items.find((n) => n.id === id);
    const wasUnread = !!target && !target.read;

    // ⬇️ İyimser: sadece unread ise rozetten 1 düşür
    if (wasUnread) optimisticMarkOneRead();

    // ⬇️ İyimser: listede de okunduya çek
    setItems((curr) =>
      unreadOnly
        ? curr.filter((n) => n.id !== id) // filtre açıkken listeden kaldır
        : curr.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    ids.current.delete(id);

    try {
      await markNotificationRead(id); // /api/notifications/:id/read
      // SSE geldiğinde (publishNotificationRead + publishUnreadCount) yine senkronlar
    } catch (e) {
      console.error(e);
      if (unreadOnly) {
        // Filtredeyken item'ı listeden kaldırmış olabiliriz; en sağlıklısı sayfayı tazelemek
        try {
          await load(true);
        } catch {}
      } else {
        // Filtre kapalıyken lokalde geri al
        setItems((curr) =>
          curr.map((n) => (n.id === id ? { ...n, read: wasUnread ? false : true } : n))
        );
      }
      // Rozeti özel olarak geri aldırmıyoruz; SSE + /count zaten senkron tutar.
    }
  }

  async function readAll() {
    // ⬇️ İyimser rozet
    optimisticMarkAllRead();

    // ⬇️ İyimser liste
    setItems((curr) => (unreadOnly ? [] : curr.map((n) => ({ ...n, read: true }))));

    try {
      await markAllNotificationsRead(); // /api/notifications/read-all
      // SSE publishNotificationsReadAll + publishUnreadCount(0) ile senkronlar
      if (unreadOnly) {
        ids.current.clear();
        setNextAfter(null);
      }
    } catch (e) {
      console.error(e);
      alert("İşlem başarısız oldu.");
      // geri alma: sayfayı baştan yüklemek en garantisi
      try {
        await load(true);
      } catch {}
    }
  }

  async function loadMore() {
    if (!nextAfter || moreLoading) return;
    setMoreLoading(true);
    try {
      await load(false);
    } catch (e) {
      console.error(e);
      setError("Daha fazla bildirim alınamadı.");
    } finally {
      setMoreLoading(false);
    }
  }

  // 🔴 SSE: canlı bildirim yayınlarını dinle (senin mevcut publisher şemanla uyumlu)
  useEffect(() => {
    // önce varsa kapat
    sseRef.current?.close();

    sseRef.current = openRealtimeStream({
      onNotification: (payload: any) => {
        if (!payload || typeof payload !== "object") return;

        // A) { kind: ... } formatı (mevcut yayınlar)
        if (payload.kind === "new") {
          setNewCount((c) => c + 1); // üstte “Yenile” barını göster
          return;
        }
        if (payload.kind === "read") {
          const id = String(payload.id ?? "");
          if (!id) return;
          setItems((curr) =>
            unreadOnly
              ? curr.filter((n) => n.id !== id)
              : curr.map((n) => (n.id === id ? { ...n, read: true } : n))
          );
          ids.current.delete(id);
          return;
        }
        if (payload.kind === "read_all") {
          setItems((curr) => (unreadOnly ? [] : curr.map((n) => ({ ...n, read: true }))));
          if (unreadOnly) {
            ids.current.clear();
            setNextAfter(null);
          }
          return;
        }
        if (payload.kind === "unread_count") {
          // Bu sayfa rozet göstermiyor; store bunu dinliyor.
          return;
        }

        // B) Minimal sinyal (type) — mention dahil
        if (
          payload.type === "like" ||
          payload.type === "comment" ||
          payload.type === "follow" ||
          payload.type === "comment_like" ||
          payload.type === "mention"
        ) {
          setNewCount((c) => c + 1);
          return;
        }
      },
    });

    return () => {
      sseRef.current?.close();
      sseRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadOnly]);

  // “Yenile” → yeni kayıtları getir, tazeleri 1.5 sn parlat
  async function showNew() {
    setNewCount(0);
    try {
      await load(true); // prev id snapshot'ı mergeItems'e iletilir → flash yapılır
    } catch (e) {
      console.error(e);
      setError("Yeni bildirimler alınamadı.");
    }
  }

  if (loading) return <div style={{ padding: 24 }}>Yükleniyor...</div>;

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: "0 auto" }}>
      <h2>{title}</h2>

      {/* Canlı bar */}
      {newCount > 0 && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #2c2c2c",
            background: "#0f1a10",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <span>✨ {newCount} yeni bildirim var.</span>
          <button onClick={showNew}>Yenile</button>
        </div>
      )}

      <div
        style={{
          marginBottom: 12,
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <button onClick={() => setUnreadOnly((v) => !v)}>
          {unreadOnly ? "Tümü" : "Sadece okunmamışlar"}
        </button>
        <button onClick={readAll}>Tümünü okundu işaretle</button>
      </div>

      {error && <div style={{ marginBottom: 12, color: "tomato" }}>{error}</div>}

      {items.length === 0 ? (
        <div>Bildirim yok.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {items.map((n) => (
            <NotificationItem
              key={n.id}
              item={n}
              onOpen={() => readOne(n.id)}
              justArrived={flashIds.has(n.id)} // ⬅️ parlama bayrağı
            />
          ))}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <button disabled={!nextAfter || moreLoading} onClick={loadMore}>
          {nextAfter ? (moreLoading ? "Yükleniyor..." : "Daha Fazla Yükle") : "Hepsi bu kadar"}
        </button>
      </div>
    </div>
  );
}
